import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {hasRole} from "../../../utils/userUtils";
import {UserContext} from "../../../context/UserContext";
import {Role} from "../../../auth/roles";
import {DatasetStatus} from "../../../model/datasetStatus";
import {fetchDatasetPage} from "../../../state/api/datasetAPI";
import DataList from "common/src/components/widget/DataList";
import {useDispatch, useSelector} from "react-redux";
import {clearInfiniteScroll, fetchGroupPage} from "../../../state/api/groupAPI";
import {constructSort} from "common/src/utils/APIUtils";
import {constructFilter} from "common/src/utils/AdvancedFilterApiUtils";
import {isEqual} from "lodash";
import {getKey} from "../../../utils/InfiniteScroleUtil";

const DatasetList = () => {
    const { t } = useTranslation();
    const {user} = useContext(UserContext);
    const [filterString, setFilterString] = useState(getKey(constructFilter([])));
    const [sortString, setSortString] = useState(getKey(constructSort({ name: "asc" })));
    const total = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.totalElements);

    const groupOptions = useSelector(state => state.groupAPI.statesInfinitiveScrolls?.[filterString]?.[sortString]?.content);
    const dispatch = useDispatch();
    const loadingGroups = useSelector(state => state.groupAPI.loading);
    const currentOptionsPage = useRef(-1);
    const optionsFilter = useRef("");
    const prefilteredOptions = useRef([]);
    const [options, setOptions] = useState([]);
    useEffect(
        () => {
            dispatch(clearInfiniteScroll());
        }, []);
    useEffect(() => {
        if (!loadingGroups && groupOptions && total != null) {
            setOptions(groupOptions);
        }
    }, [total, groupOptions, loadingGroups]);

    const onSearchQueryChange = useCallback((query) => {
            if (!isEqual(query, optionsFilter.current)) {
                optionsFilter.current = query;
                currentOptionsPage.current = -1;
            }

            const filter = query && query.trim() !== "" ? [{
                name: "name",
                operator: "LIKE_IC",
                value: `${query.trim()}`
            }] : [];
            const filterStringInternal = getKey(constructFilter(filter));
            const sortString = getKey(constructSort({ "name": "asc" }));
            setSortString(sortString);

            if (filterStringInternal && filterStringInternal !== "") {
                setFilterString(filterStringInternal);
            } else {
                setFilterString("blank");
            }
            dispatch(fetchGroupPage({
                page: {
                    page: currentOptionsPage.current >= 0 ? currentOptionsPage.current : 0,
                    size: 10,
                },
                sort: { "name": "asc" },
                filter
            }));
    }, []);

    const onFetchNextPage = () => {
        if (!loadingGroups) {
            currentOptionsPage.current += 1;
            onSearchQueryChange(optionsFilter.current, prefilteredOptions.current);
        }
    };

    return (
        <DataList
            selector={(state) => state.datasetAPI.list}
            fetchAction={fetchDatasetPage}
            loadingSelector={(state) => state.datasetAPI.loading}
            columns={[
                { name: t('dataset.name'), id: "name" },
                {
                    name: t('dataset.status'),
                    id: "status",
                    cellComponent: (dataset) => <div>{t(DatasetStatus[dataset.status])}</div>
                },
                { name: t('dataset.version'), id: "version" },
                { name: t('dataset.public'), id: "isPublic" },
                { name: t('dataset.active'), id: "isActive" },
                { name: t('dataset.created_at'), id: "createdAt" },
                { name: t('dataset.last_modified_at'), id: "lastModifiedAt" },
                { name: t('dataset.modified_by'), id: "modifiedBy" },
            ]}
            createButtonLabel={t('dataset.create')}
            showCreateButton={hasRole(user, Role.DATASET_CREATE)}
            defaultSort={"lastModifiedAt,desc"}
            filterColumns={[
                { id: "name", type: "string", name: t('dataset.name') },
                { id: "description", type: "string", name: t('dataset.description') },
                {
                    id: "status",
                    type: "select",
                    name: t('dataset.status'),
                    isMultiSelect: true,
                    onSearchQueryChange: () => {},
                    nullable: true,
                    optionsCode: "name",
                    optionsValue: "id",
                    optionsLabel: "name",
                    options: [
                        { id: "DRAFT", code: "DRAFT", name: t('dataset.status_code.draft') },
                        { id: "PUBLISHED", code: "PUBLISHED", name: t('dataset.status_code.published') }
                    ],
                    fetchMoreOptions: () => {},
                    hasMoreOptions: false,
                },
                {
                    id: "groups.id",
                    type: "findAndSelect",
                    name: t('dataset.filter.labels.groups'),
                    isMultiSelect: true,
                    options: [...(options || [])],
                    onSearchQueryChange,
                    optionsLabel: "name",
                    optionsValue: "id",
                    optionsCode: "id",
                    nullable: false,
                    loading: loadingGroups,
                    fetchMoreOptions: onFetchNextPage,
                    hasMoreOptions: (total == null || (total > 0 && [...(options || [])].length < total)),
                },

                { id: "version", type: "number", name: t('dataset.version') },
                { id: "isPublic", type: "boolean", name: t('dataset.public') },
                { id: "isActive", type: "boolean", name: t('dataset.active') },
                { id: "createdAt", type: "date", name: t('dataset.created_at') },
                { id: "lastModifiedAt", type: "date", name: t('dataset.last_modified_at') },

                { id: "modifiedBy", type: "string", name: t('dataset.modified_by') },
            ]}
        />
    );
};

// export default DatasetList;
export default DatasetList;
